import React, { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import categories from 'routes/Explore/components/Sentence/components/functions_categories.json';
import Tree from 'routes/Explore/components/Sentence/components/Tree';
import IsMultiselectContext from 'routes/Explore/components/Sentence/contexts/IsMultiselectContext';
import TreeValueContext, {
    TreeValue,
} from 'routes/Explore/components/Sentence/contexts/TreeValueContext';
import { useGetDataInTreeFormat } from 'routes/Explore/components/Sentence/hooks/treeData';
import { useGetAvailableFieldsTree } from 'routes/Explore/components/Settings/components/AvailableFieldsTree/hooks';
import MetricFilterSelect from './components/MetricFilterSelect';

type MetricFilterProps = {
    metricsFilter: string[];
    setMetricsFilter: (arg: string[]) => void;
};

const MetricFilter = ({
    metricsFilter,
    setMetricsFilter,
}: MetricFilterProps): JSX.Element => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    const treeValueContextProviderValue = useMemo(
        () => ({
            currentValues: metricsFilter,
            setCurrentValues: setMetricsFilter,
        }),
        [metricsFilter, setMetricsFilter],
    );

    const { data: availableFields, isLoading } = useGetAvailableFieldsTree();
    const treeData = useGetDataInTreeFormat(availableFields ?? [], categories);

    const openModal = () => setOpen(true);
    const closeModal = () => setOpen(false);

    const onClickTree = (fieldName: TreeValue) => {
        const newSelectedFilterOptions = metricsFilter.includes(fieldName)
            ? metricsFilter.filter((f) => f !== fieldName)
            : [...metricsFilter, fieldName];
        setMetricsFilter(newSelectedFilterOptions);
    };

    const label = t('indicators.filterType.metric');

    return (
        <IsMultiselectContext.Provider value>
            <TreeValueContext.Provider value={treeValueContextProviderValue}>
                <MetricFilterSelect
                    open={open}
                    closeModal={closeModal}
                    openModal={openModal}
                    label={label}
                    selectedFilterOptions={metricsFilter}
                    setSelectedFilterOptions={setMetricsFilter}
                />
                <Tree
                    treeData={treeData}
                    isLoading={isLoading}
                    testid="indicator-metric-filter"
                    onClick={onClickTree}
                    onClose={closeModal}
                    open={open}
                    showConfirmButton
                    isConfirmButtonDisabled={false}
                    onSubmit={closeModal}
                />
            </TreeValueContext.Provider>
        </IsMultiselectContext.Provider>
    );
};

export default memo(MetricFilter);
